import { IExcelReportData, ITableData } from 'types/excelExport';
import {
    ICashFlowEntityNameValue,
    IDispositionData,
    IOperatingPeriodCashFlows,
    IProFormaReturnsData,
    IUsesSummary,
} from 'views/ProFormaTable/types';
import {
    createDataRow,
    createBlankRow,
    parseNumber,
    createCashFlowRow,
    createDarkSectionHeader,
    createMonthlyHeaderRow,
} from './helpers';
import { EXCEL_COLORS, EXCLUDED_CASH_FLOW_DEVELOPMENT_BUDGET_LABELS } from './constants';
import { getModelExportConfig } from './modelConfig';
import assertNever from 'helpers/assertNever';
import { IncomeCalculationType } from 'constants/proForma';

interface ICashFlowSheetParams {
    dealName: string;
    cashFlows: (IOperatingPeriodCashFlows | null)[];
    operationBeginMonth: number | undefined;
    returns: IProFormaReturnsData;
    disposition: IDispositionData | undefined;
    usesSummary: IUsesSummary;
    proFormaModelId: number;
    showCents?: boolean;
    includeGainLossToLease?: boolean;
    incomeCalculationType?: string;
}

const createSectionHeader = createDarkSectionHeader;

// Build one row per name/value entity in `items` (operating expenses,
// capital expenditures, etc.), pulling each period's value by index off the
// supplied accessor and flipping the sign so costs display as outflows.
const createNegatedIndexedCostRows = (
    items: ICashFlowEntityNameValue[] | undefined,
    getItems: (cf: IOperatingPeriodCashFlows) => ICashFlowEntityNameValue[] | undefined,
    cashFlows: (IOperatingPeriodCashFlows | null)[],
    showCents: boolean,
): ITableData[] => {
    if (!items) return [];
    const indexByName = new Map<string, number>();
    items.forEach((item, idx) => indexByName.set(item.name, idx));
    return items.map(item => {
        const idx = indexByName.get(item.name);
        return createCashFlowRow(
            item.name,
            cashFlows,
            cf => {
                const raw = idx !== undefined ? getItems(cf)?.[idx]?.value : undefined;
                return -(parseNumber(raw) ?? 0);
            },
            showCents,
        );
    });
};

export const formatCashFlowSheet = ({
    dealName,
    cashFlows,
    operationBeginMonth,
    returns,
    disposition,
    usesSummary,
    proFormaModelId,
    showCents = false,
    includeGainLossToLease = true,
    incomeCalculationType,
}: ICashFlowSheetParams): IExcelReportData => {
    const config = getModelExportConfig(proFormaModelId);
    const { cashFlowTab } = config;

    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    // Clamp returns cash flow to the sale month. The backend emits extra months
    // beyond sale; the Returns tab UI applies the same truncation via maxIndex.
    // Guard against a null netSaleProceeds (the backend schema allows null, even
    // though the TS type does not) so we don't mis-clamp to the first period.
    const returnsSource = returns.cashFlow ?? [];
    const saleIndex = returnsSource.findIndex(cf => {
        const value = parseNumber(cf.netSaleProceeds);
        return value !== null && value !== 0;
    });
    const returnsCashFlows = saleIndex === -1
        ? returnsSource
        : returnsSource.slice(0, saleIndex + 1);
    const totalMonths = returnsCashFlows.length;
    const colCount = totalMonths + 1;

    // Pad operating period cash flows into the full analysis-start-to-sale timeline.
    // Pre-operating and post-operating columns get null; existing rows coerce null to 0.
    const validCashFlows = cashFlows.filter(cf => cf !== null) as IOperatingPeriodCashFlows[];
    const paddedOperating: (IOperatingPeriodCashFlows | null)[] = Array(totalMonths).fill(null);
    const operatingStart = operationBeginMonth ?? 0;
    validCashFlows.forEach((cf, i) => {
        const idx = operatingStart + i;
        if (idx >= 0 && idx < totalMonths) {
            paddedOperating[idx] = cf;
        }
    });
    const firstOperatingCashFlow = paddedOperating.find(cf => cf !== null) ?? null;

    // Map YYYY-MM -> column index to align usesSummary periods with the timeline.
    const dateToColIndex = new Map<string, number>();
    returnsCashFlows.forEach((cf, i) => {
        const key = cf.analysisDate?.substring(0, 7);
        if (key) dateToColIndex.set(key, i);
    });

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: 'Pro Forma Cash Flow Schedule', cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(colCount),
        createMonthlyHeaderRow(returnsCashFlows),
    ];

    // ============================================================
    // DEVELOPMENT BUDGET
    // Development costs are stored positive in the backend but displayed as
    // outflows (negative) on the Cash Flow Schedule so a SUM down the column
    // ticks back to Net Levered Cash Flow.
    // ============================================================
    const developmentBudgetRows: ITableData[] = [
        createSectionHeader(cashFlowTab.developmentBudget.sectionHeader, colCount),
    ];

    usesSummary?.uses?.forEach(use => {
        const displayName = use.description || use.nodeName || '';
        if (EXCLUDED_CASH_FLOW_DEVELOPMENT_BUDGET_LABELS.has(displayName)) return;
        const values: number[] = Array(totalMonths).fill(0);
        use.periods?.forEach(period => {
            const key = period.periodStartDate?.substring(0, 7);
            if (!key) return;
            const idx = dateToColIndex.get(key);
            if (idx !== undefined) {
                values[idx] = -(parseNumber(period.projected) ?? 0);
            }
        });
        developmentBudgetRows.push(createCashFlowRow(
            displayName,
            values.map(v => ({ v })),
            cf => cf.v,
            showCents,
        ));
    });

    developmentBudgetRows.push(createCashFlowRow(
        cashFlowTab.developmentBudget.totalDevelopmentCosts,
        returnsCashFlows,
        cf => -(parseNumber(cf.projectCost) ?? 0),
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));

    // ============================================================
    // CONSTRUCTION FINANCING
    // Sign flip: interest and financing fees are stored positive but
    // presented as outflows to match the Returns tab convention.
    // Construction Loan Payoff is already signed negative on ICashFlowPeriod.
    // ============================================================
    const constructionFinancingRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.constructionFinancing.sectionHeader, colCount),
        createCashFlowRow(
            cashFlowTab.constructionFinancing.loanDraws,
            returnsCashFlows,
            cf => cf.constructionLoanDraws,
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.constructionFinancing.interest,
            returnsCashFlows,
            cf => -(parseNumber(cf.constructionLoanInterest) ?? 0),
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.constructionFinancing.financingFees,
            returnsCashFlows,
            cf => -(parseNumber(cf.financingFee) ?? 0),
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.constructionFinancing.constructionLoanPayoff,
            returnsCashFlows,
            cf => cf.constructionLoanPayoff,
            showCents,
        ),
    ];

    // ============================================================
    // INCOME
    // ============================================================
    const incomeRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.income.sectionHeader, colCount),
    ];

    const addTenantAndFloorIncomeRows = (
        getTenants: (cf: IOperatingPeriodCashFlows) => ICashFlowEntityNameValue[] | undefined,
    ): void => {
        if (!firstOperatingCashFlow) return;

        const tenants = getTenants(firstOperatingCashFlow);
        if (incomeCalculationType === IncomeCalculationType.TENANTS && tenants) {
            const tenantIndexMap = new Map<string, number>();
            tenants.forEach((item, idx) => {
                tenantIndexMap.set(item.name, idx);
            });
            tenants.forEach(tenant => {
                const idx = tenantIndexMap.get(tenant.name);
                incomeRows.push(createCashFlowRow(
                    tenant.name,
                    paddedOperating,
                    cf => (idx !== undefined ? getTenants(cf)?.[idx]?.value : undefined),
                    showCents,
                ));
            });
        } else if (firstOperatingCashFlow.incomeFloorEstimates) {
            firstOperatingCashFlow.incomeFloorEstimates.forEach((floorEstimate, idx) => {
                incomeRows.push(createCashFlowRow(
                    `Floor ${floorEstimate.floor}`,
                    paddedOperating,
                    cf => cf.incomeFloorEstimates?.[idx]?.value,
                    showCents,
                ));
            });
        }
    };

    // Helper: emit per-line-item rows (other income, income adjustments, etc.)
    // by reading the names from the first operating cash flow as the source of
    // truth and then pulling each period's value off the same index.
    const pushNamedLineItemRows = (
        getItems: (cf: IOperatingPeriodCashFlows) => ICashFlowEntityNameValue[] | undefined,
    ): void => {
        if (!firstOperatingCashFlow) return;
        const items = getItems(firstOperatingCashFlow);
        if (!items) return;
        items.forEach((item, idx) => {
            incomeRows.push(createCashFlowRow(
                item.name,
                paddedOperating,
                cf => getItems(cf)?.[idx]?.value,
                showCents,
            ));
        });
    };

    if (cashFlowTab.income.type === 'apartment_with_retail') {
        // ============================================================
        // APARTMENT INCOME (listed first, above retail)
        // grossRent on apartment-with-retail cashflows is the combined
        // apt+retail figure; subtract totalRetailIncome to isolate the
        // apartment-only subtotal. Same logic for totalRentalIncome and
        // totalPotentialIncome.
        // ============================================================
        const apartmentGrossRent = (cf: IOperatingPeriodCashFlows): number =>
            (parseNumber(cf.grossRent) ?? 0) - (parseNumber(cf.totalRetailIncome) ?? 0);

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.grossRentLabel, paddedOperating, apartmentGrossRent, showCents,
        ));

        if (cashFlowTab.income.concessions) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.concessions, paddedOperating, cf => cf.concessions, showCents,
            ));
        }
        if (cashFlowTab.income.nonRevenueUnits) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.nonRevenueUnits, paddedOperating, cf => cf.nonRevenueUnits, showCents,
            ));
        }
        if (cashFlowTab.income.gainLossToLease && includeGainLossToLease) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.gainLossToLease, paddedOperating, cf => parseNumber(cf.gainLossToLease), showCents,
            ));
        }

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.totalRentalIncome,
            paddedOperating,
            cf => (parseNumber(cf.totalRentalIncome) ?? 0) - (parseNumber(cf.totalRetailIncome) ?? 0),
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        pushNamedLineItemRows(cf => cf.otherIncome);

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.totalOtherIncome,
            paddedOperating, cf => cf.totalOtherIncome,
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.apartmentTotalPotentialIncome,
            paddedOperating,
            cf => (parseNumber(cf.totalPotentialIncome) ?? 0) - (parseNumber(cf.totalRetailIncome) ?? 0),
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        pushNamedLineItemRows(cf => cf.incomeAdjustments);
        incomeRows.push(createCashFlowRow(
            cashFlowTab.adjustments.totalIncomeAdjustments,
            paddedOperating, cf => cf.totalIncomeAdjustments,
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        // ============================================================
        // RETAIL INCOME (listed below apartment block)
        // ============================================================
        incomeRows.push(createSectionHeader(cashFlowTab.income.retailSubsectionHeader, colCount));
        addTenantAndFloorIncomeRows(cf => cf.incomeTenants);

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.retailTotalRentalIncome,
            paddedOperating, cf => cf.totalRetailIncome,
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        pushNamedLineItemRows(cf => cf.retailOtherIncome);

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.retailTotalOtherIncomeLabel,
            paddedOperating, cf => cf.retailTotalOtherIncome,
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        if (cashFlowTab.income.expenseReimbursements) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.expenseReimbursements,
                paddedOperating, cf => cf.totalRecoveryIncome,
                showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
            ));
            incomeRows.push(createBlankRow(colCount));
        }

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.retailTotalPotentialIncome,
            paddedOperating, cf => cf.retailTotalPotentialIncome,
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        pushNamedLineItemRows(cf => cf.retailIncomeAdjustments);

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.retailTotalIncomeAdjustments,
            paddedOperating, cf => cf.retailTotalIncomeAdjustments,
            showCents, true, false, EXCEL_COLORS.GRAY_HEADER,
        ));
    } else {
        switch (cashFlowTab.income.type) {
            case 'gross_rent':
                incomeRows.push(
                    createCashFlowRow(cashFlowTab.income.grossRentLabel, paddedOperating, cf => cf.grossRent, showCents),
                );
                break;

            case 'tenants':
                addTenantAndFloorIncomeRows(cf => cf.incomeTenants);
                break;

            case 'tenants_data_center':
                addTenantAndFloorIncomeRows(cf => cf.incomeTenantsDataCenter);
                break;

            default:
                assertNever(cashFlowTab.income);
        }

        if (cashFlowTab.income.concessions) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.concessions, paddedOperating, cf => cf.concessions, showCents,
            ));
        }

        if (cashFlowTab.income.gainLossToLease && includeGainLossToLease) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.gainLossToLease, paddedOperating, cf => parseNumber(cf.gainLossToLease), showCents,
            ));
        }

        if (cashFlowTab.income.nonRevenueUnits) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.nonRevenueUnits, paddedOperating, cf => cf.nonRevenueUnits, showCents,
            ));
        }

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.totalRentalIncome,
            paddedOperating,
            cf => cf.totalRentalIncome,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        pushNamedLineItemRows(cf => cf.otherIncome);

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.totalOtherIncome,
            paddedOperating,
            cf => cf.totalOtherIncome,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
        ));
        incomeRows.push(createBlankRow(colCount));

        if (cashFlowTab.income.expenseReimbursements) {
            incomeRows.push(createCashFlowRow(
                cashFlowTab.income.expenseReimbursements,
                paddedOperating,
                cf => cf.totalRecoveryIncome,
                showCents,
                true,
                false,
                EXCEL_COLORS.GRAY_HEADER,
            ));
            incomeRows.push(createBlankRow(colCount));
        }

        incomeRows.push(createCashFlowRow(
            cashFlowTab.income.totalPotentialIncome,
            paddedOperating,
            cf => cf.totalPotentialIncome,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
        ));
    }

    // ============================================================
    // INCOME ADJUSTMENTS
    // ============================================================
    const adjustmentsRows: ITableData[] = [createBlankRow(colCount)];

    if (cashFlowTab.income.type !== 'apartment_with_retail') {
        adjustmentsRows.push(createSectionHeader(cashFlowTab.adjustments.sectionHeader, colCount));

        const incomeAdjustmentsIndexMap = new Map<string, number>();
        firstOperatingCashFlow?.incomeAdjustments?.forEach((item, idx) => {
            incomeAdjustmentsIndexMap.set(item.name, idx);
        });

        if (firstOperatingCashFlow?.incomeAdjustments) {
            firstOperatingCashFlow.incomeAdjustments.forEach(adjustment => {
                const idx = incomeAdjustmentsIndexMap.get(adjustment.name);
                adjustmentsRows.push(createCashFlowRow(
                    adjustment.name,
                    paddedOperating,
                    cf => (idx !== undefined ? cf.incomeAdjustments?.[idx]?.value : undefined),
                    showCents,
                ));
            });
        }

        adjustmentsRows.push(createCashFlowRow(
            cashFlowTab.adjustments.totalIncomeAdjustments,
            paddedOperating,
            cf => cf.totalIncomeAdjustments,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
        ));
        adjustmentsRows.push(createBlankRow(colCount));
    }

    adjustmentsRows.push(createCashFlowRow(
        cashFlowTab.effectiveGrossRevenue.sectionHeader,
        paddedOperating,
        cf => cf.effectiveGrossRevenue,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
        EXCEL_COLORS.DARK_GRAY,
    ));

    // ============================================================
    // OPERATING EXPENSES
    // Stored positive in the backend but displayed as outflows (negative) to
    // keep costs consistently signed down the column.
    // ============================================================
    const expenseRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.expenses.sectionHeader, colCount),
    ];

    expenseRows.push(...createNegatedIndexedCostRows(
        firstOperatingCashFlow?.operatingExpenses,
        cf => cf.operatingExpenses,
        paddedOperating,
        showCents,
    ));

    expenseRows.push(createCashFlowRow(
        cashFlowTab.expenses.totalOperatingExpenses,
        paddedOperating,
        cf => -(parseNumber(cf.totalOperatingExpenses) ?? 0),
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));
    expenseRows.push(createBlankRow(colCount));
    expenseRows.push(createCashFlowRow(
        cashFlowTab.netOperatingIncome.sectionHeader,
        paddedOperating,
        cf => cf.netOperatingIncome,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
        EXCEL_COLORS.DARK_GRAY,
    ));

    // ============================================================
    // CAPITAL EXPENDITURES + CASH FLOW FROM OPERATIONS
    // Stored positive in the backend but displayed as outflows (negative).
    // ============================================================
    const capexRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.capex.sectionHeader, colCount),
    ];

    capexRows.push(...createNegatedIndexedCostRows(
        firstOperatingCashFlow?.capitalExpenditures,
        cf => cf.capitalExpenditures,
        paddedOperating,
        showCents,
    ));

    capexRows.push(createCashFlowRow(
        cashFlowTab.capex.totalCapitalExpenditures,
        paddedOperating,
        cf => -(parseNumber(cf.totalCapitalExpenditures) ?? 0),
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
    ));
    capexRows.push(createBlankRow(colCount));
    capexRows.push(createCashFlowRow(
        cashFlowTab.cashFlowFromOperations.sectionHeader,
        paddedOperating,
        cf => cf.cashFlowFromOperations,
        showCents,
        true,
        false,
        EXCEL_COLORS.GRAY_HEADER,
        EXCEL_COLORS.DARK_GRAY,
    ));

    // ============================================================
    // PERMANENT DEBT
    // Closing costs and payoff are already signed negative on ICashFlowPeriod.
    // Debt service is stored positive and displayed negated.
    // ============================================================
    const permanentDebtRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.permanentDebt.sectionHeader, colCount),
        createCashFlowRow(
            cashFlowTab.permanentDebt.funding,
            returnsCashFlows,
            cf => cf.permanentLoanFunding,
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.permanentDebt.closingCosts,
            returnsCashFlows,
            cf => cf.permanentLoanClosingCosts,
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.permanentDebt.debtService,
            returnsCashFlows,
            cf => -(parseNumber(cf.permanentLoanDebtService) ?? 0),
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.permanentDebt.payoff,
            returnsCashFlows,
            cf => cf.permanentLoanPayoff,
            showCents,
        ),
    ];

    // ============================================================
    // SALE PROCEEDS
    // Gross sale proceeds and selling costs are placed only at the sale month
    // column. Net sale proceeds comes from returns.cashFlow[i].netSaleProceeds.
    // When no sale period is detected, leave these rows as zeros so the
    // section stays internally consistent with the (all-zero) net sale row.
    // ============================================================
    const grossProceedsValues: number[] = Array(totalMonths).fill(0);
    const sellingCostsValues: number[] = Array(totalMonths).fill(0);
    if (saleIndex !== -1 && saleIndex < totalMonths) {
        grossProceedsValues[saleIndex] = parseNumber(disposition?.grossSaleProceeds?.amount) ?? 0;
        sellingCostsValues[saleIndex] = -(parseNumber(disposition?.sellingCosts?.amount) ?? 0);
    }

    const saleProceedsRows: ITableData[] = [
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.saleProceeds.sectionHeader, colCount),
        createCashFlowRow(
            cashFlowTab.saleProceeds.grossSaleProceeds,
            grossProceedsValues.map(v => ({ v })),
            cf => cf.v,
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.saleProceeds.sellingCosts,
            sellingCostsValues.map(v => ({ v })),
            cf => cf.v,
            showCents,
        ),
        createCashFlowRow(
            cashFlowTab.saleProceeds.netSaleProceeds,
            returnsCashFlows,
            cf => cf.netSaleProceeds,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
        ),
    ];

    // ============================================================
    // NET LEVERED CASH FLOW (bottom line)
    // ============================================================
    const netLeveredCashFlowRows: ITableData[] = [
        createBlankRow(colCount),
        createCashFlowRow(
            cashFlowTab.netLeveredCashFlow.label,
            returnsCashFlows,
            cf => cf.netLeveredCashFlow,
            showCents,
            true,
            false,
            EXCEL_COLORS.GRAY_HEADER,
            EXCEL_COLORS.DARK_GRAY,
        ),
    ];

    // ============================================================
    // OCCUPANCY METRICS
    // ============================================================
    const occupancyRows: ITableData[] = [
        createBlankRow(colCount),
        createBlankRow(colCount),
        createSectionHeader(cashFlowTab.occupancyMetrics.sectionHeader, colCount),
    ];

    if (cashFlowTab.occupancyMetrics.leasedPercent) {
        occupancyRows.push(createCashFlowRow(
            cashFlowTab.occupancyMetrics.leasedPercent,
            paddedOperating,
            cf => parseNumber(cf.leasedPercentage),
            showCents,
            false,
            true,
        ));
    }

    if (cashFlowTab.occupancyMetrics.economicOccupancy) {
        occupancyRows.push(createCashFlowRow(
            cashFlowTab.occupancyMetrics.economicOccupancy,
            paddedOperating,
            cf => parseNumber(cf.economicOccupancyPercentage),
            showCents,
            false,
            true,
        ));
    }

    return {
        topHeaderRows,
        reportTables: [
            {
                tableHeaderRows: [],
                tableData: [
                    ...developmentBudgetRows,
                    ...constructionFinancingRows,
                    ...incomeRows,
                    ...adjustmentsRows,
                    ...expenseRows,
                    ...capexRows,
                    ...permanentDebtRows,
                    ...saleProceedsRows,
                    ...netLeveredCashFlowRows,
                    ...occupancyRows,
                ],
            },
        ],
    };
};
